import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { SortByProperty } from '@fi-shared/utils/sort-by-property';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { MediaBankService } from '@fi-app/media-bank/media-bank.service';
import { UserService } from '@fi-app/user/user.service';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { FiTagsListPositionTypes, FiTagsSelectionTypes, FiTagsSortType } from '@fi-app/components/items-list/enums';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ListViewSettingsService } from '@fi-app/utilities/fi-services/list-view-settings.service';
import {
    AppearanceModuleSideListOptions,
    IAppearanceModuleSideListOption,
} from '../../apeparance-module-side-list-options';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { isDefined } from '@fi-shared/utils/is-defined';
import {
    ExtendedSearchTypesService,
    IExtendedSearchSettings,
} from '@fi-app/extended-search/extended-search-types.service';
import { OperationOverlayLargeNg4Service } from '@fi-app/utilities/operation-overlay-large/operation-overlay-large.service';
import { promiseUnwrap } from '@fi-shared/fi-shared-services/extensions.service';
import { ExtendedSearchListComponent } from '@fi-app/extended-search/extended-search-list/extended-search-list.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ListViewSettingModel } from '@fi-app/components/items-list/list-view-setting.model';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { IListViewTag } from '@fi-shared/fi-items-list/fi-items-list';
import { IAppearanceSidebarStateModel } from '@fi-app/appearance/appearance';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiUserListContainerDialogComponent } from '@fi-app/user/fi-user-list-container-dialog/fi-user-list-container-dialog.component';

interface IAvailableTagsByModule {
    moduleType: ModuleType;
    tags: IListViewTag[];
}

interface IListViewPosition {
    Id: FiTagsListPositionTypes;
    Name: string;
}

interface IListViewTags {
    Id: FiTagsSortType;
    Name: string;
}

@Component({
    selector: 'fi-appearance-list-view-edit',
    templateUrl: './list-view-edit.component.html',
    styleUrls: ['./list-view-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-appearance-list-view-edit' },
    standalone: false,
})
export class FiAppearanceListViewEditComponent implements OnInit {
    @Input() siteDesignId: number;
    @Input() settings: ListViewSettingModel[];
    @Input() appearanceSidebarStateModel: IAppearanceSidebarStateModel;

    selectedSettings: ListViewSettingModel;
    sideListOptions = AppearanceModuleSideListOptions.filter((item) => item.moduleType !== ModuleType.Document);
    selectedSideListOption: IAppearanceModuleSideListOption;

    listPositionsListData = new DropdownSettingsModelV2<IListViewPosition>({
        filterBy: 'Name',
    });

    tagsSortTypeListData = new DropdownSettingsModelV2<IListViewTags>({
        filterBy: 'Name',
    });

    tagSelectionTypes = [
        { Id: FiTagsSelectionTypes.AllTags, TextId: 'ccmAppearance.listView.allTags' },
        { Id: FiTagsSelectionTypes.SelectedTags, TextId: 'ccmAppearance.listView.selectionOfTags' },
    ];

    selectedTagsData: {
        [prop: number]: number[]; // selectedTagIds for each module type (documents, files, our organisation)
    } = {};

    isSaving: boolean = false;
    editTabs: EditTabsModel;
    moreMenuItems = new MoreMenuMobileSettingsModel();
    tagsDataSource: Promise<IListViewTag[]>;
    availableTagsByModules: IAvailableTagsByModule[] = [];

    constructor(
        private mediaBankService: MediaBankService,
        private userService: UserService,
        private appTextService: AppTextService,
        private loadingService: LoadingService,
        private listViewSettingsService: ListViewSettingsService,
        private saveChangesWarningService: SaveChangesWarningService,
        private extendedSearchTypesService: ExtendedSearchTypesService,
        private operationOverlayLargeService: OperationOverlayLargeNg4Service,
        private stateService: FiStateService,
        private detectDevicesService: DetectDevicesService,
        private fiDialogsService: FiDialogsService,
    ) {}

    ngOnInit() {
        this.setPageHeaderData();
        this.setListDataSettings();
        this.setTagsByModuleType();

        this.settings.forEach((setting) => {
            this.selectedTagsData[setting.ModuleType] = setting.ListViewTags.map((tag) => tag.Id);
        });
    }

    getTagsByModuleType(moduleType: ModuleType): IListViewTag[] {
        const tagsResult = this.availableTagsByModules.find((availableTags: IAvailableTagsByModule) => {
            return availableTags.moduleType === moduleType;
        });
        return tagsResult ? tagsResult.tags : [];
    }

    isSettingSelected(setting: ListViewSettingModel): boolean {
        return setting.ObjectType === this.selectedSettings.ObjectType;
    }

    save() {
        if (this.isSaving) return false;

        this.loadingService
            .trackPromise(this.listViewSettingsService.saveSettings(this.settings, this.siteDesignId))
            .then(() => {
                this.saveChangesWarningService.stop();
                this.stateService.go(FiStateNames.OtherSettings.SiteDesign.BrandEdit, { Id: this.siteDesignId });
            });
    }

    selectSideListOption(sideListOption: IAppearanceModuleSideListOption) {
        this.appearanceSidebarStateModel.modulePreviewTemplateName = sideListOption.previewTemplateName;
        this.selectedSideListOption = sideListOption;

        this.selectedSettings = this.settings.find((item: ListViewSettingModel) => {
            return item.ModuleType === sideListOption.moduleType && item.ObjectType === sideListOption.objectType;
        });
    }

    isSideListOptionSelected(sideListOption: IAppearanceModuleSideListOption): boolean {
        return sideListOption.objectType === this.selectedSideListOption.objectType;
    }

    previewListNew() {
        if (this.selectedSettings.ObjectType !== ObjectTypes.User) {
            this.previewMediaBank();
            return;
        }

        const userListSettings: ListViewSettingModel = this.settings.find((item: ListViewSettingModel) => {
            return item.ObjectType === ObjectTypes.User;
        });
        this.fiDialogsService.custom({
            isTopCloseButtonShown: true,
            hideFooter: true,
            contentComponentRef: FiUserListContainerDialogComponent,
            contentComponentData: userListSettings,
            config: {
                minWidth: '80%',
                maxWidth: '80%',
                autoFocus: false,
                panelClass: 'fi-users-list-container-dialog-panel',
            },
        });
    }

    private previewMediaBank() {
        const previewSettings: IExtendedSearchSettings = this.extendedSearchTypesService.getExtendedSearchSettings(
            this.selectedSettings.ObjectType,
        );
        if (!isDefined(previewSettings)) {
            throw 'FI: Unable to open preview - unknown object type';
        }

        const resolve = Object.assign({}, previewSettings.listSettings.resolve);
        resolve.mediaListViewSettings = this.settings.find((item) => item.ObjectType === ObjectTypes.MediaBank);
        resolve.showAllTagsList = this.isAllTagsList();
        resolve.allowSelection = false;
        const copy = Object.assign({}, previewSettings, {
            listSettings: Object.assign({}, previewSettings.listSettings, {
                resolve: resolve,
            }),
        });

        promiseUnwrap(copy.listSettings.resolve).then((resolvedData) => {
            copy.listSettings.resolve = resolvedData;
            this.operationOverlayLargeService.openOperationOverlayLargeDialog(ExtendedSearchListComponent, copy, {
                showInsert: false,
            });
        });
    }

    isTagsSortable(): boolean {
        return this.selectedSettings.SortType === FiTagsSortType.Manual;
    }

    isAllTagsList(): boolean {
        return this.selectedSettings.ListType === FiTagsSelectionTypes.AllTags;
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    onSelectedTagsChange(setting: ListViewSettingModel, selectedTagIds: number[]) {
        setting.ListViewTags = this.getTagsByModuleType(setting.ModuleType).filter(
            (tag) => selectedTagIds.indexOf(tag.Id) !== -1,
        );
    }

    private setTagsByModuleType() {
        this.settings.forEach((setting: ListViewSettingModel) => {
            setting.TagsDataSource().then((result: IListViewTag[]) => {
                this.availableTagsByModules.push({
                    tags: result,
                    moduleType: setting.ModuleType,
                });
            });
        });
    }

    private setPageHeaderData() {
        this.editTabs = new EditTabsModel(
            this.appTextService.get('common.appearance'),
            this.appTextService.get('common.cancel'),
        );
        this.sideListOptions.forEach((sideListOption: IAppearanceModuleSideListOption) => {
            this.moreMenuItems.AddListItem(this.appTextService.get(sideListOption.textId), () =>
                this.selectSideListOption(sideListOption),
            );
        });
    }

    private setListDataSettings() {
        this.settings = SortByProperty(this.settings, 'ObjectType', true);
        const promises: Promise<any>[] = [];
        this.settings.forEach((setting: ListViewSettingModel) => {
            switch (setting.ObjectType) {
                case ObjectTypes.MediaBank:
                    {
                        setting.textId = 'common.files';
                        setting.ModuleType = ModuleType.MediaBank;
                        setting.TagsDataSource = () => this.mediaBankService.getMediaBankFileTagsByUserAccessRights();

                        promises.push(
                            this.mediaBankService
                                .getMediaBankFileTagsByUserAccessRights()
                                .then((data) => setting.excludeUnavailableTags(data)),
                        );
                    }
                    break;
                case ObjectTypes.User:
                    {
                        setting.textId = 'common.our_organisation';
                        setting.ModuleType = ModuleType.Core;
                        setting.TagsDataSource = () => this.userService.getUserTagsByUserAccessRights();

                        promises.push(
                            this.userService
                                .getUserTagsByUserAccessRights()
                                .then((data) => setting.excludeUnavailableTags(data)),
                        );
                    }
                    break;
                default:
                    setting.textId = 0;
            }
        });

        this.selectedSettings = this.settings[0];
        this.selectedSideListOption = this.sideListOptions[0];

        this.listPositionsListData.setDataSet([
            { Id: FiTagsListPositionTypes.Top, Name: this.appTextService.get('common.top') },
            { Id: FiTagsListPositionTypes.Left, Name: this.appTextService.get('common.left') },
        ]);

        this.tagsSortTypeListData.setDataSet([
            {
                Id: FiTagsSortType.Alphabetical,
                Name: this.appTextService.get('ccmAppearance.listView.alphabeticalOrder'),
            },
            { Id: FiTagsSortType.Manual, Name: this.appTextService.get('ccmAppearance.listView.manualOrder') },
        ]);

        this.sideListOptions.forEach((option) => {
            if (option.previewTemplateName === this.appearanceSidebarStateModel.modulePreviewTemplateName) {
                this.selectSideListOption(option);
            }
        });

        Promise.all(promises).then(() => {
            this.saveChangesWarningService.start(() => this.getModelData());
        });
    }

    private getModelData(): { items: string } {
        return {
            items: JSON.stringify(this.settings),
        };
    }
}
